import React, { useEffect, useMemo, useState } from "react";
import CandleChart from "../../candle/CandleChart";
import { describeCandleLocation, elementAnchor } from "../../candle/candleModel";
import { buildReveal } from "../../core/revealModel";
import NoWickDiagram from "./NoWickDiagram";
import { correctionLine } from "./corrections";
import { conceptMarkers } from "./explain";
import Score from "./Score";
import Sentences from "../../core/Sentences";
import { PROMPTS } from "./config";

const FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A]";

// The reveal after a submit with one or more mistakes (client: "Reveal Path
// Restructure"). Same content as before, paced:
//
//   Round 1 (paced)     RESULTS            "X of 6 correct", only the correct
//                                          elements on the candle
//                       REVIEW CORRECTIONS one card per mistake: your tap, the
//                                          correct position, one plain line
//                       LEARN AND RETRY    explanation, no-wick examples, Retry
//   Round 2+ (compact)  one screen: correct elements left, corrections right,
//                       the explanation only for concepts not yet explained,
//                       no no-wick card, Retry.
//
// Every marker is placed from data: the learner's recorded tap and the true
// position from the candle's OHLC (see core/revealModel.js).
//
//   explanations  [{ key, text }] concept explanations not shown before (gated
//                 by the Read); each is drawn next to a candle with the positions
//                 it talks about marked in gold
//   showNoWick    include the no-wick examples (round 1 only)
//   ctaLabel      "Retry", or "Continue" once no fresh candle is left
export default function FailureReveal({ ohlc, results, paced, explanations, showNoWick, ctaLabel, onContinue }) {
  const reveal = useMemo(
    () =>
      buildReveal(PROMPTS, results, {
        anchorOf: (id) => elementAnchor(ohlc, id),
        describe: (pt) => describeCandleLocation(ohlc, pt),
      }),
    [ohlc, results]
  );
  const [step, setStep] = useState(paced ? "results" : "compact");

  // Each step is a new screen: start it at the top.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [step]);

  // No "Correct" under the labels: the "X of 6 correct" line and the teal
  // check already say it.
  const correctMarkers = reveal.correct.map((c) => ({
    key: `${c.id}-ok`,
    x: c.tap.x,
    y: c.tap.y,
    state: "correct",
    label: c.label,
    sub: null,
    side: "left",
  }));
  const scoreLine = <Score correct={reveal.correct} total={reveal.total} />;

  if (step === "results") {
    return (
      <Screen label="Results">
        {scoreLine}
        <div className="mx-auto mt-1 md:mt-3 w-full max-w-[440px]">
          <CandleChart ohlc={ohlc} disabled markers={correctMarkers} />
        </div>
        <Cta onClick={() => setStep("corrections")}>Review Corrections</Cta>
      </Screen>
    );
  }

  if (step === "corrections") {
    return (
      <Screen label="Review corrections">
        {reveal.mistakes.map((m) => (
          <div key={m.id} className="mt-3 rounded-xl border border-gray-200 px-4 py-3">
            <p className="font-semibold text-bodyInk">{m.label}</p>
            <Sentences text={correctionLine(m)} className="mt-0.5 text-sm text-bodyInk" />
            {/* Small candle on mobile; desktop has room to show it larger. */}
            <div className="mx-auto mt-2 w-full max-w-[440px]">
              <CandleChart
                ohlc={ohlc}
                disabled
                heights={{ mobile: 230, desktop: 320 }}
                markers={[
                  {
                    key: "fix",
                    x: m.anchor.x,
                    y: m.anchor.y,
                    state: "corrected",
                    label: "Correct answer",
                    sub: m.correctPlace.short,
                    side: "right",
                  },
                  {
                    key: "you",
                    x: m.tap.x,
                    y: m.tap.y,
                    state: "incorrect",
                    label: "Your answer",
                    sub: m.yourPlace.short,
                    side: "right",
                  },
                ]}
              />
            </div>
          </div>
        ))}
        <Cta onClick={() => setStep("learn")}>See Why</Cta>
      </Screen>
    );
  }

  if (step === "learn") {
    return (
      <Screen label="Learn and retry">
        {explanations.map((e) => (
          <ConceptCard key={e.key} ohlc={ohlc} concept={e} />
        ))}
        {showNoWick && <NoWickDiagram />}
        <Cta onClick={onContinue}>{ctaLabel}</Cta>
      </Screen>
    );
  }

  // Compact (round 2 and later): one screen.
  const correctionMarkers = reveal.mistakes.flatMap((m) => [
    { key: `${m.id}-you`, x: m.tap.x, y: m.tap.y, state: "incorrect", label: `Your ${m.label}`, sub: null, side: "right" },
    {
      key: `${m.id}-fix`,
      x: m.anchor.x,
      y: m.anchor.y,
      state: "corrected",
      label: `Correct ${m.label}`,
      sub: null,
      side: "right",
    },
  ]);
  return (
    <Screen>
      {scoreLine}
      <div className="mx-auto mt-1 md:mt-3 w-full max-w-[440px]">
        <CandleChart ohlc={ohlc} disabled markers={[...correctMarkers, ...correctionMarkers]} />
      </div>
      <ul className="sr-only">
        {reveal.mistakes.map((m) => (
          <li key={m.id}>
            {m.label}: {correctionLine(m)}
          </li>
        ))}
      </ul>
      {explanations.map((e) => (
        <ConceptCard key={e.key} ohlc={ohlc} concept={e} />
      ))}
      <Cta onClick={onContinue}>{ctaLabel}</Cta>
    </Screen>
  );
}

// One concept explanation, anchored to the candle it talks about: the text,
// then the candle with the positions it names marked as gold diamonds
// (client: "Words annotate the picture; they don't replace it").
function ConceptCard({ ohlc, concept }) {
  const markers = conceptMarkers(ohlc, concept.key).map((m) => ({
    key: m.id,
    x: m.point.x,
    y: m.point.y,
    state: "corrected",
    label: m.label,
    sub: null,
    side: "right",
  }));
  return (
    <div className="mt-3 rounded-xl border border-gray-200 px-4 py-3">
      <p className="font-semibold text-bodyInk">Explanation</p>
      <Sentences text={concept.text} className="mt-1 text-sm text-bodyInk" />
      <div className="mx-auto mt-2 w-full max-w-[440px]">
        <CandleChart ohlc={ohlc} disabled heights={{ mobile: 230, desktop: 320 }} markers={markers} />
      </div>
    </div>
  );
}

function Screen({ label, children }) {
  return (
    <div>
      <h1 className="font-montserrat font-bold text-title-subpage text-pageTitle">Candle Anatomy</h1>
      {label && <p className="mt-0.5 text-xs font-semibold uppercase tracking-wider text-secondary">{label}</p>}
      {children}
    </div>
  );
}

function Cta({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mt-4 w-full min-h-[44px] rounded-lg bg-primaryColor text-white font-semibold ${FOCUS}`}
    >
      {children}
    </button>
  );
}
